import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, AlertTriangle, TrendingDown, TrendingUp, Package } from "lucide-react";
import { ProductTable } from "@/components/estoque/ProductTable";
import { AddProductDialog } from "@/components/estoque/AddProductDialog";
import { StockHistory } from "@/components/estoque/StockHistory";
import { StockMovementDialog } from "@/components/estoque/StockMovementDialog";
import { useEstoqueResumo } from "@/features/estoque/hooks/useEstoquePosicao";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

export default function Estoque() {
  const { temPermissao } = usePermissoes();
  const podeIncluir = temPermissao(FORMULARIOS.AJUSTE_SALDO, 'incluir');

  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEntradaDialogOpen, setIsEntradaDialogOpen] = useState(false);
  const [isSaidaDialogOpen, setIsSaidaDialogOpen] = useState(false);

  const { data: resumo, isLoading: isLoadingResumo } = useEstoqueResumo();

  const stockStats = [
    {
      title: "Total de Produtos",
      value: isLoadingResumo ? "—" : String(resumo?.totalProdutos ?? 0),
      change: "Produtos ativos",
      icon: Package,
      trend: "up",
    },
    {
      title: "Produtos em Alerta",
      value: isLoadingResumo ? "—" : String(resumo?.produtosEmAlerta ?? 0),
      change: "Controlam estoque sem saldo",
      icon: AlertTriangle,
      trend: "warning",
    },
    {
      title: "Valor Total",
      value: isLoadingResumo ? "—" : formatCurrency(resumo?.valorTotalEstoque ?? 0),
      change: "Preço de venda (lotes ativos)",
      icon: TrendingUp,
      trend: "up",
    },
    {
      title: "Produtos Vencendo",
      value: isLoadingResumo ? "—" : String(resumo?.produtosVencendo ?? 0),
      change: "Próximos 7 dias",
      icon: TrendingDown,
      trend: "warning",
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Gestão de Estoque</h1>
          <p className="text-muted-foreground mt-1">Controle completo de produtos e movimentações</p>
        </div>
        {podeIncluir && (
        <div className="flex gap-2">
          <Button
            onClick={() => setIsEntradaDialogOpen(true)}
            variant="outline"
            className="gap-2 border-success text-success hover:bg-success hover:text-white"
          >
            <TrendingUp className="h-4 w-4" />
            Entrada
          </Button>
          <Button
            onClick={() => setIsSaidaDialogOpen(true)}
            variant="outline"
            className="gap-2 border-destructive text-destructive hover:bg-destructive hover:text-white"
          >
            <TrendingDown className="h-4 w-4" />
            Saída
          </Button>
          <Button onClick={() => setIsAddDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Novo Produto
          </Button>
        </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stockStats.map((stat) => (
          <Card key={stat.title} className="shadow-soft hover:shadow-medium transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon className={`h-4 w-4 ${
                stat.trend === "up" 
                  ? "text-success" 
                  : stat.trend === "warning" 
                  ? "text-warning" 
                  : "text-muted-foreground"
              }`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">{stat.value}</div>
              <p className={`text-xs mt-1 ${
                stat.trend === "up" 
                  ? "text-success" 
                  : stat.trend === "warning" 
                  ? "text-warning" 
                  : "text-muted-foreground"
              }`}>
                {stat.change}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle>Produtos em Estoque</CardTitle>
          <CardDescription>Gerencie seu inventário com filtros avançados</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, código ou fornecedor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          <ProductTable
            searchTerm={searchTerm}
            categoryFilter="all"
            supplierFilter="all"
          />
        </CardContent>
      </Card>

      <StockHistory />

      <AddProductDialog 
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
      />

      <StockMovementDialog
        open={isEntradaDialogOpen}
        onOpenChange={setIsEntradaDialogOpen}
        type="entrada"
      />

      <StockMovementDialog
        open={isSaidaDialogOpen}
        onOpenChange={setIsSaidaDialogOpen}
        type="saida"
      />
    </div>
  );
}
